"use client";

import React, {
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  createElement,
} from "react";
import { InputInfo } from "./InputInfo";
import { Label } from "./Label";
import { cn } from "@repo/shared/utils";
import type { LucideIcon } from "lucide-react";
import { useNestedComponent } from "./hooks/useNestedComponent";

export interface InputProps
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  id: string;
  label?: React.ReactNode;
  inputStyle?: string;
  labelStyle?: string;
  containerStyle?: string;
  error?: boolean;
  errorMessage?: string;
  orientation?: "horizontal" | "vertical";
  icon?: LucideIcon;
  wrapper?: React.ComponentType<{ children: React.ReactNode }>;
  inserts?: {
    after?: React.ReactNode;
  };
}

const DynamicIcon = ({ icon }: { icon: LucideIcon }) => {
  return createElement(icon, {
    size: 20,
    className: "absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 transform",
  });
};

export const Input = ({
  id,
  label,
  inputStyle,
  labelStyle,
  containerStyle,
  error,
  errorMessage,
  orientation = "vertical",
  icon,
  inserts,
  className,
  wrapper,
  ...props
}: InputProps) => {
  const Wrapper = useNestedComponent(
    wrapper ?? (({ children }: { children: React.ReactNode }) => children)
  );

  return (
    <div
      className={cn(
        "flex gap-y-1 mt-1.5",
        orientation === "horizontal"
          ? "flex-row items-center gap-x-3"
          : "flex-col",
        containerStyle
      )}
    >
      {label && <Label className={labelStyle} label={label} id={id} />}
      <div className="flex flex-col gap-1 w-full">
        <div className="flex flex-row items-center relative flex-grow w-full">
          {icon && <DynamicIcon icon={icon} />}
          <Wrapper>
            <input
              id={id}
              className={cn(
                "flex text-form-foreground h-10 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                error && "border-destructive focus:accent-destructive border-2",
                inputStyle,
                className,
                icon && "pl-8"
              )}
              {...props}
            />
          </Wrapper>
          {inserts?.after}
        </div>
        {error && errorMessage && <InputInfo info={errorMessage} />}
      </div>
    </div>
  );
};
Input.displayName = "Input";
